import { Alert, Modal, Pending } from '@cluey/cluey-components';
import { capitalize } from 'lodash';
import posthog from 'posthog-js';
import { Fragment, useState } from 'react';
import { useHistory } from 'react-router';

import { fetchEnrolment } from '../../../actions/hub/hubEnrolmentManagementActions';
import SessionTag from '../../../common/SessionTag/SessionTag';
import { Loader } from '../../../components/Loader';
import { useGetSessionPolicy } from '../../../hooks/queries/useSessionQuery';
import type { Enrolment, Session } from '../../../types/hubInterfaces';
import { isGroupSessionCheck } from '../../../util/helpers';
import { useActions } from '../../../util/hooks/useActions';
import {
  PATH_HUB_CR_CHANGE_TUTOR,
  PATH_HUB_CR_OTHER_QUERY,
  PATH_HUB_CR_PAUSE_SESSIONS,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE,
  PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP,
} from '../../../util/pagePath';
import useCanPauseEnrolment from '../../../v2/features/pause-enrolment/hooks/useCanPauseEnrolment';
import { OptionCard, OptionCardContent } from '../../EnrolmentManagement/OptionCard';
import { IntentIntro } from '../../IntentIntro';

interface Props {
  enrolment: Enrolment;
  session: Session;
  open: boolean;
  closeModal(): void;
}

const ChangeSessionModal = ({ enrolment, session, open, closeModal }: Props) => {
  const { student, tutor, subject, uniqueEnrolmentId } = session;
  const getEnrolmentDetails = useActions(fetchEnrolment);
  const history = useHistory();
  const possessiveNoun = student ? `${student}'s` : 'your';
  const noun = subject ? `${subject} session` : 'session';
  const redirectToWithToken = (path: string, uniqueEnrolmentId: string, queryParam?: URLSearchParams) => {
    if (queryParam) {
      history.push(`${path}?${queryParam.toString()}&token=${uniqueEnrolmentId}`);
    } else {
      history.push(`${path}?token=${uniqueEnrolmentId}`);
    }
  };
  const bodyText = `${capitalize(possessiveNoun)} ${noun} (with ${tutor})`;

  const isGroupSession = isGroupSessionCheck(session.type);
  const {
    data: sessionPolicy,
    isLoading: isPolicyLoading,
    error: policyError,
  } = useGetSessionPolicy({ sessionId: session?.studentSessionId, enabled: open });
  const canBeRescheduled = sessionPolicy?.reschedule?.isAllowed;

  const {
    data: enrolmentPolicy,
    isLoading: isEnrolmentPolicyLoading,
    error: enrolmentPolicyError,
  } = useGetSessionPolicy({ enrolmentId: enrolment.enrolmentSfid, enabled: open && !isPolicyLoading });
  const enrolmentCanBePaused = useCanPauseEnrolment(enrolment.enrolmentSfid, enrolmentPolicy);

  const createHeading = () => {
    if (policyError || enrolmentPolicyError) {
      return (
        <Alert
          className="mt-2"
          type="error"
          body={`There was an error retrieving this session/enrolment policy. ${policyError || enrolmentPolicyError}`}
        />
      );
    }
    if (canBeRescheduled) {
      return (
        <IntentIntro className="mb-10 flex flex-row items-center gap-x-3 !text-lg">
          {bodyText} <SessionTag session={session} policy={sessionPolicy} />
        </IntentIntro>
      );
    } else {
      return (
        <Fragment>
          <IntentIntro className="mb-10 flex flex-row items-center gap-x-3 !text-lg">
            {bodyText} <SessionTag session={session} policy={sessionPolicy} />
          </IntentIntro>
          <div className="mb-10 text-xs font-bold uppercase text-red-5">
            {sessionPolicy?.reschedule?.externalMessage}
          </div>
        </Fragment>
      );
    }
  };

  return (
    <Modal open={open} setOpen={closeModal} closeOnBackground accent closeButton>
      <Pending loading={isPolicyLoading || isEnrolmentPolicyLoading} loader={<Loader />}>
        <div className="px-12 py-14">
          <h2 className="pb-6 font-display text-2xl font-bold tracking-[-0.02rem] lg:text-4xl">
            Manage ongoing sessions
          </h2>
          {createHeading()}
          <OptionCard
            onClickHandler={() => {
              getEnrolmentDetails({ enrolmentId: uniqueEnrolmentId });
              posthog.capture('click_manage-session_change-schedule');
              history.push(
                isGroupSession
                  ? PATH_HUB_ENROLMENT_CHANGE_SCHEDULE_GROUP
                  : `${PATH_HUB_ENROLMENT_CHANGE_SCHEDULE}?token=${uniqueEnrolmentId}`
              );
            }}
          >
            <OptionCardContent>Change ongoing schedule</OptionCardContent>
          </OptionCard>

          {enrolmentCanBePaused && (
            <OptionCard
              onClickHandler={() => {
                posthog.capture('click_manage-session_pause-enrolment');
                redirectToWithToken(PATH_HUB_CR_PAUSE_SESSIONS, uniqueEnrolmentId);
              }}
              disabled={!enrolmentCanBePaused}
            >
              <OptionCardContent disabled={!enrolmentCanBePaused}>Pause enrolment</OptionCardContent>
            </OptionCard>
          )}

          {/* <OptionCard
            onClickHandler={() => {
              posthog.capture('click_manage-session_cancel-enrolment');
              redirectToWithToken(cancelEnrolmentBasePath, uniqueEnrolmentId, new URLSearchParams({ view: 'form' }));
            }}
          >
            <OptionCardContent>Cancel enrolment</OptionCardContent>
          </OptionCard> */}

          <OptionCard
            onClickHandler={() => {
              posthog.capture('click_manage-session_change-tutor');
              redirectToWithToken(PATH_HUB_CR_CHANGE_TUTOR, uniqueEnrolmentId);
            }}
          >
            <OptionCardContent>{`Provide feedback, or request a change regarding ${possessiveNoun} tutor`}</OptionCardContent>
          </OptionCard>

          <OptionCard
            onClickHandler={() => {
              posthog.capture('click_manage-session_something-else');
              redirectToWithToken(PATH_HUB_CR_OTHER_QUERY, uniqueEnrolmentId);
            }}
          >
            <OptionCardContent>Something else</OptionCardContent>
          </OptionCard>
        </div>
      </Pending>
    </Modal>
  );
};

export const useChangeSessionModal = () => {
  const [open, setOpen] = useState(false);
  const openModal = () => setOpen(true);
  const closeModal = () => setOpen(false);

  return {
    open,
    openModal,
    closeModal,
    ChangeSessionModal,
  };
};

export default ChangeSessionModal;
